import React, { useMemo, useState } from 'react';

import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import PlayArrowOutlinedIcon from '@material-ui/icons/PlayArrowOutlined';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import StopOutlinedIcon from '@material-ui/icons/StopOutlined';
import Alert from '@material-ui/lab/Alert';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import ActionTemplatePrintTemplate from 'components/_print/_templates/ActionTemplatePrintTemplate/ActionTemplatePrintTemplate';
import PrintComponent from 'components/_print/PrintComponent/PrintComponent';
import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton';
import FormButtons from 'components/FormButtons';
import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import type { ActionTemplate } from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';
import useActionTemplateCopy from 'pages/ActionLibrary/hooks/useActionTemplateCopy';
import useIsActionTemplateOwner from 'pages/ActionLibrary/hooks/useIsActionTemplateOwner';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import PATHS from 'router/PATHS';

type Props = {
  template: ActionTemplate;
  onDelete: () => void;
  onCopy?: (newTemplate: ActionTemplate) => void;
};

const ActionTemplateActions: React.FC<Props> = ({ template, onDelete, onCopy }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { showConfirmationModal } = useConfirmationModalContext();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const { copyActionTemplate, loading: copyLoading } = useActionTemplateCopy();
  const isActionTemplateOwner = useIsActionTemplateOwner();
  const hasDraft = template.hasImpendingChanges;

  const isLocked = useMemo(() => {
    if (isActionTemplateOwner(template)) return false;
    return !!template.isLocked;
  }, [isActionTemplateOwner, template]);

  const isCurrentUserEditing = useMemo(() => !!template.isLocked && isActionTemplateOwner(template), [template, isActionTemplateOwner]);

  const handleToggleActive = async () => {
    try {
      setLoading(true);
      const newStatus = template.status === 'active' ? 'inactive' : 'active';
      const { data } = await actionTemplatesApi.updateActionTemplate(template.id, { status: newStatus });
      enqueueSnackbar(
        newStatus === 'active' ? t(action_library_messages.success.activated) : t(action_library_messages.success.deactivated),
        { variant: 'success' },
      );
      if (onCopy) onCopy(data);
      window.location.reload();
    } catch (error) {
      enqueueSnackbar(t(action_library_messages.errors.template_status_update_failed), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    await copyActionTemplate(template);
    if (onCopy) onCopy(template); // We might need to adjust onCopy if it expects the new template, but usually reload handles it
  };

  const handleEdit = () => {
    history.push(PATHS.ACTION_LIBRARY_EDIT.replace(':id', template.id.toString()));
  };

  const handleDelete = async () => {
    const confirmed = await showConfirmationModal({
      title: t(action_library_messages.delete_confirmation.title),
      body: t(action_library_messages.delete_confirmation.body),
    });

    if (confirmed) {
      try {
        setLoading(true);
        await actionTemplatesApi.deleteActionTemplate(template.id);
        enqueueSnackbar(t(action_library_messages.success.deleted), { variant: 'success' });
        onDelete();
      } catch (error) {
        enqueueSnackbar(t(action_library_messages.errors.template_delete_failed), { variant: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <CenteredGrid width='lg' withoutPadding>
      {hasDraft && (
        <Box mb={2} width='100%'>
          <Alert severity='info'>
            {t(
              action_library_messages.draft_activation_warning,
              'This template has unpublished changes. Activation applies to the published version only.',
            )}
          </Alert>
        </Box>
      )}
      <FormButtons inline={!isMobile}>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={loading || (template.status !== 'active' && isCurrentUserEditing)}
          icon={false}
          onClick={handleToggleActive}
          showLoader={false}
          startIcon={template.status === 'active' ? <StopOutlinedIcon /> : <PlayArrowOutlinedIcon />}
          textLike={false}
          variant='outlined'
        >
          {template.status === 'active' ? t(action_library_messages.actions.deactivate) : t(action_library_messages.actions.activate)}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={loading || copyLoading}
          icon={false}
          onClick={handleCopy}
          showLoader={false}
          startIcon={<FileCopyOutlinedIcon />}
          textLike={false}
          variant='outlined'
        >
          {t(action_library_messages.actions.copy)}
        </ColoredButton>
        <PrintComponent
          ctaRenderer={({ onPrint }) => (
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={loading}
              icon={false}
              onClick={onPrint}
              showLoader={false}
              startIcon={<PrintOutlinedIcon />}
              textLike={false}
              variant='outlined'
            >
              {t(action_library_messages.actions.print)}
            </ColoredButton>
          )}
          templateComponent={<ActionTemplatePrintTemplate template={template} />}
        />
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={loading || isLocked}
          icon={false}
          onClick={handleEdit}
          showLoader={false}
          startIcon={<EditOutlinedIcon />}
          textLike={false}
          variant='outlined'
        >
          {t(action_library_messages.actions.edit)}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='danger'
          disabled={loading || isLocked}
          icon={false}
          onClick={handleDelete}
          showLoader={false}
          startIcon={<DeleteOutlinedIcon />}
          textLike={false}
          variant='outlined'
        >
          {t(action_library_messages.actions.delete)}
        </ColoredButton>
      </FormButtons>
    </CenteredGrid>
  );
};

export default ActionTemplateActions;
